import _ from 'lodash'
import { DateTime } from 'luxon'
import { GenericLoadStopProgress, Job, JobListJob } from 'shared/models/job'
import {
  LoadStop,
  LoadStopProgressStatus,
  LoadStopType,
  LocationVector,
  TrackingStatus,
} from 'shared/models/load'

import { EquipmentType } from '../models/equipment'
import { getTimeDifference, getTimeDifferenceFromNowString, replaceUnderscore } from './formatters'

const TOO_EARLY_THRESHOLD_HOURS = 12
const INCORRECT_ETA_THRESHOLD_HOURS = 2

export const getETA = (
  showHosEta: boolean,
  loadStopProgress: GenericLoadStopProgress | undefined
): DateTime | null => {
  // Early return if no progress data
  if (!loadStopProgress) {
    return null
  }

  // Check for HOS ETA first if enabled
  const hosEta = loadStopProgress.providerEta?.providerEta
  if (showHosEta && hosEta) {
    return DateTime.fromISO(hosEta)
  }

  // Fall back to regular ETA
  const regularEta = loadStopProgress.dateEstimatedArrival
  if (regularEta) {
    return DateTime.fromISO(regularEta)
  }

  return null
}

const isTooEarlyETA = (loadStopProgress: GenericLoadStopProgress, etaTime: DateTime | null) => {
  if (!etaTime) {
    return false
  }
  const expectedEarliestArrivalTime = loadStopProgress?.stop.dateStart
    ? DateTime.fromISO(loadStopProgress?.stop.dateStart)
    : null
  if (!expectedEarliestArrivalTime) {
    return false
  }
  const hoursDifference = Math.abs(etaTime.diff(expectedEarliestArrivalTime, 'hours').hours)
  // ETA is too early if it's more than 12 hours before the expected earliest arrival time
  return hoursDifference > TOO_EARLY_THRESHOLD_HOURS
}

const isTooLateETA = (loadStopProgress: GenericLoadStopProgress, etaTime: DateTime | null) => {
  if (!etaTime) {
    return false
  }
  const expectedLatestArrivalTime = loadStopProgress?.stop.dateEnd
    ? DateTime.fromISO(loadStopProgress?.stop.dateEnd)
    : null
  if (!expectedLatestArrivalTime) {
    return false
  }
  const hoursDifference = Math.abs(etaTime.diff(expectedLatestArrivalTime, 'hours').hours)
  // ETA is too late if it's more than 2 hours after the expected latest arrival time
  return hoursDifference > INCORRECT_ETA_THRESHOLD_HOURS
}

export const showETAOnBrokerView = <T extends GenericLoadStopProgress>(
  isBrokerView: boolean,
  trackingStatus: TrackingStatus,
  showHosEta: boolean,
  loadStopProgress: T | undefined,
  dateArrived: DateTime | null,
  dateDeparted: DateTime | null
) => {
  // If this is not a broker view or there is no load stop progress, don't show the ETA
  if (!isBrokerView || !loadStopProgress) {
    return false
  }

  // If the driver has already arrived or departed, don't show the ETA
  if (dateArrived || dateDeparted) {
    return false
  }

  // If the tracking status is an error, stale location, or no location, don't show the ETA
  if (
    [TrackingStatus.ERROR, TrackingStatus.STALE_LOCATION, TrackingStatus.NO_LOCATION].includes(trackingStatus)
  ) {
    return false
  }

  // Fetch the HOS ETA or estimated ETA. Don't show the ETA if it doesn't exist
  const etaTime = getETA(showHosEta, loadStopProgress)
  if (!etaTime) {
    return false
  }

  // If the driver has already arrived or departed, don't show the ETA
  const hasArrivedAtStop = [LoadStopProgressStatus.AT_FACILITY, LoadStopProgressStatus.DEPARTED].includes(
    loadStopProgress.status
  )
  if (hasArrivedAtStop) {
    return false
  }

  // Don't show the ETA if it's too early or too late
  const isTooEarly = isTooEarlyETA(loadStopProgress, etaTime)
  const isTooLate = isTooLateETA(loadStopProgress, etaTime)
  if (isTooEarly || isTooLate) {
    return false
  }
  return true
}

export const commandCenterTrackingStatusToDisplay = (job: JobListJob): string => {
  // Check if job has tracking status fields
  const eldTrackingStatus = job.eldTrackingStatus
  const hosEldTrackingStatus = job.hosEldTrackingStatus

  // If no tracking status available, return "Tracking unavailable"
  if (!eldTrackingStatus && !hosEldTrackingStatus) {
    return 'Tracking unavailable'
  }

  // Use HOS ELD tracking status if available, otherwise fall back to regular ELD tracking status
  const trackingStatus = hosEldTrackingStatus || eldTrackingStatus

  if (!trackingStatus) {
    return 'Tracking unavailable'
  }

  // Map tracking status to display text based on requirements
  switch (trackingStatus) {
    case TrackingStatus.EARLY:
      return 'Early'
    case TrackingStatus.ON_TIME:
      return 'On time'
    case TrackingStatus.MAYBE_LATE:
      return 'Maybe late'
    case TrackingStatus.LATE:
      return 'Late'
    case TrackingStatus.AT_FACILITY:
      return 'At facility'
    case TrackingStatus.COMPLETED:
      return 'Departed'
    case TrackingStatus.ERROR:
      return 'Tracking unavailable'
    case TrackingStatus.STALE_LOCATION:
      return 'Tracking unavailable'
    case TrackingStatus.NO_LOCATION:
      return 'No location'
    default:
      return 'Scheduled'
  }
}

export const getJobTrackingStatusToDisplay = <T extends GenericLoadStopProgress>(
  trackingStatus: TrackingStatus,
  loadStopProgress: T | undefined,
  showHosEta: boolean,
  isBrokerView: boolean,
  latestTruckLocationVector?: LocationVector
): string => {
  if (loadStopProgress?.status === LoadStopProgressStatus.AT_FACILITY) {
    return 'Arrived'
  }

  if (loadStopProgress?.status === LoadStopProgressStatus.SKIPPED) {
    return 'Not detected'
  }

  const isEnRoute = loadStopProgress?.status === LoadStopProgressStatus.EN_ROUTE

  if (isEnRoute && trackingStatus === TrackingStatus.ERROR) {
    return 'Tracking unavailable'
  }

  if (isEnRoute && trackingStatus === TrackingStatus.STALE_LOCATION) {
    if (isBrokerView) {
      return 'Tracking unavailable'
    }
    return `Stale ${
      latestTruckLocationVector
        ? `(${getTimeDifferenceFromNowString(latestTruckLocationVector?.timestamp, false, true)})`
        : ''
    }`
  }

  if (isEnRoute && trackingStatus === TrackingStatus.NO_LOCATION) {
    if (isBrokerView) {
      return 'Tracking unavailable'
    }
    return 'No location'
  }

  if (isEnRoute && isBrokerView) {
    const etaTime = getETA(showHosEta, loadStopProgress)
    const isTooEarly = isTooEarlyETA(loadStopProgress, etaTime)
    const isTooLate = isTooLateETA(loadStopProgress, etaTime)
    if (isTooEarly || isTooLate) {
      return 'In transit'
    }
  }

  if (
    isBrokerView &&
    isEnRoute &&
    [TrackingStatus.STALE_LOCATION, TrackingStatus.NO_LOCATION].includes(trackingStatus)
  ) {
    return 'Tracking unavailable'
  }

  const isLate = [TrackingStatus.LATE, TrackingStatus.MAYBE_LATE].includes(trackingStatus)
  if (isEnRoute && isLate && showHosEta && loadStopProgress?.providerEta) {
    return `Late - ${getTimeDifference(
      loadStopProgress?.providerEta?.providerEta,
      loadStopProgress?.stop.dateEnd
    )}`
  }

  if (isEnRoute) {
    return _.startCase(trackingStatus.toLowerCase())
  }

  if (loadStopProgress) {
    return _.startCase(loadStopProgress.status.toLowerCase())
  }

  return ''
}

export const getLoadStopProgressIsManualCheckin = (loadStopProgress: GenericLoadStopProgress): boolean => {
  if (loadStopProgress?.status === LoadStopProgressStatus.AT_FACILITY) {
    return loadStopProgress?.arrivalStopCheckin?.trigger === 'MANUAL'
  }
  if (loadStopProgress?.status === LoadStopProgressStatus.DEPARTED) {
    return loadStopProgress?.departureStopCheckin?.trigger === 'MANUAL'
  }
  return false
}

export const isDateFromManualCheckin = (
  loadStopProgress: GenericLoadStopProgress,
  dateType: 'arrival' | 'departure'
) => {
  if (dateType === 'arrival') {
    return loadStopProgress?.arrivalStopCheckin?.trigger === 'MANUAL'
  }
  if (dateType === 'departure') {
    return loadStopProgress?.departureStopCheckin?.trigger === 'MANUAL'
  }
  return false
}

export const getTripRate = (loadTripRate: number | string, confirmedRate?: number): number => {
  if (confirmedRate) {
    return confirmedRate
  }
  return loadTripRate && loadTripRate !== '0.00' ? Number(loadTripRate) : 0
}

export const formatTripRate = (tripRate: number): string => {
  return Number(tripRate).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  })
}

export const getTripRatePerMile = (tripRate: number, tripDistance: number): string => {
  return tripRate && tripDistance ? Number(tripRate / tripDistance).toFixed(2) : '0'
}

export const getLoadStopOrigin = (job?: Job) => {
  if (!job) {
    return null
  }
  const origin = job.load.stops.find((stop) => stop.type === LoadStopType.ORIGIN) as LoadStop
  return origin || null
}

export const formatEquipments = (equipments: EquipmentType[]) => {
  return !!equipments && equipments.length > 0 ? replaceUnderscore(equipments[0].toLowerCase()) : ''
}

export const formatLoadStopDate = (date: DateTime, timezone: string) =>
  date.setZone(timezone).toLocaleString({
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    timeZoneName: 'short',
    hourCycle: 'h23',
  })

export const stopAppointmentWindow = (stop: LoadStop) => {
  const localizedDateStart = formatLoadStopDate(DateTime.fromISO(stop.dateStart), stop.locationTimezone)
  const localizedDateEnd = formatLoadStopDate(DateTime.fromISO(stop.dateEnd), stop.locationTimezone)
  return localizedDateStart !== localizedDateEnd
    ? `${localizedDateStart} - ${localizedDateEnd}`
    : localizedDateStart
}
